import type { TVersionFilter } from "@/dialogs/share-dialog/types";
import { FEATURE_FLAGS, isOnFeatureFlag } from "@lib/feature-flags";
import { versionsKeys } from "@/shared/services";
import type { IVersion } from "@/types";
import { reactQueryUtils } from "@lib/react-query";
import type { RootState } from "@store";
import {
  createVersion,
  deleteVersion,
  setVersions,
  updatePageOgImage,
  updateVersion,
} from "@store/share-dialog";
import { setVersion as setStoryVersion } from "@store/story";
import { toast } from "react-toastify";
import { resolveDateFilter } from "@pages/stories/store/stories.filters";
import { api } from "./api";

const path = (path: string) => `/versions${path}`;

type TGetVersionsCountParams = {
  pageId: string;
  omitSealed?: boolean;
  status?: string;
  userId?: string;
};

type TGetVersionsCountResponse = {
  count: number;
};

/** GET /versions/send-quota (backend versions/send-quota.js). */
export type TSendQuota = {
  per: "month" | "day";
  limit: number;
  used: number;
  remaining: number;
  plan: "trial" | "starter" | "pro" | "team";
};

export type TBulkSendJob = {
  status: "running" | "done" | "failed";
  total: number;
  rows: {
    title: string;
    email?: string | null;
    versionId?: string | null;
    status: "pending" | "created" | "sent" | "error";
    error?: string | null;
  }[];
  csvLink?: string;
};

export type TSearchVersionsParams = TVersionFilter & {
  pageId: string;
  // optional: when provided, backend will exclude this ID (exceptId)
  primaryVersionId?: string;
  page?: number;
  // cache-buster used only in client cache key (not sent to backend)
  refreshSeq?: number;
};

export type TPagedVersions = {
  items: IVersion[];
  hasMore: boolean;
};

export type TUpdateVersionArgs = {
  versionId: string;
  payload: Record<string, any>;
  isSyncToMaster?: boolean;
  expireAt?: string | null;
  /** Days before expireAt to email the recipients; null clears it. */
  expiryReminderDaysBefore?: number | null;
  /** The caller toasts its own failure (no second, generic one). */
  silent?: boolean;
};

// -------- Shared cache utilities (DRY helpers for cache iteration and matching) --------
// Note: these are function declarations (not const) so they are hoisted. They reference
// versionsApi at call time, which is defined below by the time these are invoked.

/** Return all cached args (originalArgs) for the paged search for a specific pageId */
function getCachedSearchArgsForPage(state: RootState, pageId: string) {
  const entries = versionsApi.util.selectInvalidatedBy(state, [
    { type: "VersionSearch" as const, id: pageId },
  ]);
  return entries
    .filter((q) => q.endpointName === "searchVersionsPaged")
    .map((q) => q.originalArgs as TSearchVersionsParams);
}

/** Return all cached args (originalArgs) for the paged search across all filters */
function getAllCachedSearchArgs(state: RootState) {
  const entries = versionsApi.util.selectInvalidatedBy(state, [
    { type: "VersionSearchAll" as const, id: "all" },
  ]);
  return entries
    .filter((q) => q.endpointName === "searchVersionsPaged")
    .map((q) => q.originalArgs as TSearchVersionsParams);
}

/** Purge all cached paged-search entries for a given pageId (clear items and reset hasMore). */
export function purgeSearchCacheForPage(
  dispatch: any,
  getState: () => RootState,
  pageId: string,
) {
  const cachedArgs = getCachedSearchArgsForPage(getState(), pageId);
  for (const a of cachedArgs) {
    dispatch(
      versionsApi.util.updateQueryData(
        "searchVersionsPaged",
        a as any,
        (draft: TPagedVersions) => {
          draft.items = [];
          draft.hasMore = true;
        },
      ),
    );
  }

  // Invalidate the search queries so they are refetched on next use
  dispatch(
    versionsApi.util.invalidateTags([
      { type: "VersionSearch" as const, id: pageId },
    ]),
  );
}

/** Title-only search match, case-insensitive substring */
function titleMatchesSearch(
  title: string | undefined,
  search: string | undefined,
) {
  const s = (search ?? "").trim().toLowerCase();
  if (!s) return true;
  return (title ?? "").toLowerCase().includes(s);
}

/** Does a version match given query args (search by title + optional userIds) */
function matchesFiltersForArgs(v: IVersion, args: TSearchVersionsParams) {
  // Exclude the primary version from any cached list for this page
  // Backend already omits it via `exceptId`, but client-side cache updates
  // must respect the same rule to avoid inserting it locally on updates.
  if (args.primaryVersionId && v._id === args.primaryVersionId) return false;
  if (!titleMatchesSearch((v as any)?.data?.title, args.search)) return false;
  if (Array.isArray(args.userIds) && args.userIds.length > 0) {
    const ownerId = (v as any).userId;
    if (!ownerId || !args.userIds.includes(ownerId)) return false;
  }
  return true;
}

export const versionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // `recordView` puts the version on the caller's Recently viewed. Opt-in, as
    // for pages: only the editor opening a version is an open. Part of the cache
    // key on purpose — a fetch that recorded and one that did not are different
    // requests.
    getVersionById: builder.query<
      IVersion,
      { id: string; recordView?: boolean }
    >({
      query: ({ id, recordView }) => ({
        url: path(`/${id}`),
        method: "GET",
        params: recordView ? { recordView: true } : undefined,
      }),
      keepUnusedDataFor: 60,
      providesTags: (_result, _error, { id }) => [{ type: "Version", id }],
    }),
    getPageVersions: builder.query({
      query: ({
        status,
        sender,
        limit,
        offset,
        id,
        omitSealed = false,
      }: {
        status: string;
        sender?: string;
        omitSealed?: boolean;
        limit: number;
        offset: number;
        id: string;
      }) => ({
        url: `/pages/${id}/versions`,
        method: "GET",
        params: {
          status,
          limit,
          sender,
          offset,
          omitSealed,
        },
      }),
      providesTags: [{ type: "Versions" }],
    }),
    /**
     * Versions for the slides library's "Import from stories" view: the
     * versions of the given stories (a folder's), or every version in the
     * org when no stories are given. Newest first, a page at a time: one cache
     * entry per scope and search, with each further page appended to it, the
     * way the share dialog's own paged search works below.
     */
    searchVersionsForImport: builder.query<
      TPagedVersions,
      { pageIds?: string[]; search?: string; size?: number; page?: number }
    >({
      query: ({ pageIds, search = "", size = 24, page = 0 }) => ({
        url: path("/search"),
        method: "GET",
        params: {
          ...(pageIds && pageIds.length ? { pageIds: pageIds.join(",") } : {}),
          search,
          size,
          page,
          sort: "descending",
          order: "updatedAt",
        },
      }),
      transformResponse: (resp: IVersion[], _meta, arg) => ({
        items: resp,
        hasMore: resp.length === (arg.size ?? 24),
      }),
      // JSON.stringify drops the undefined `page`: same key as before, minus the page.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}|${JSON.stringify({ ...queryArgs, page: undefined })}`,
      merge: (currentCache, newCache, { arg }) => {
        if ((arg.page ?? 0) === 0) {
          currentCache.items = newCache.items;
        } else {
          const existing = new Set(currentCache.items.map((v) => v._id));
          currentCache.items.push(
            ...newCache.items.filter((v) => !existing.has(v._id)),
          );
        }
        currentCache.hasMore = newCache.hasMore;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
      providesTags: [{ type: "Versions" }],
    }),
    searchVersions: builder.query({
      query: ({
        pageId,
        search = "",
        size = 25,
        page = 0,
        sort,
        userId,
        order,
      }) => ({
        url: path("/search"),
        method: "GET",
        params: {
          pageId,
          search,
          size,
          page,
          sort,
          order,
          userId,
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const page = (getState() as RootState).shareDialog.page;
          const primaryVersionId = page ? page.primaryVersionId : null;
          const filtered = data.filter(
            (v: IVersion) => v._id !== primaryVersionId,
          );
          if (args.isLoad) {
            const versions: IVersion[] = (getState() as RootState).shareDialog
              .versions;
            dispatch(setVersions([...versions, ...filtered]));
          } else {
            dispatch(setVersions(filtered));
          }
        } catch {
          toast.error("Failed search version");
        }
      },
    }),
    searchVersionsPaged: builder.query<TPagedVersions, TSearchVersionsParams>({
      query: (params: TSearchVersionsParams) => ({
        url: path("/search"),
        method: "GET",
        params: {
          pageId: params.pageId,
          search: params.search || "",
          size: params.size || 25,
          page: params.page || 0,
          sort: params.sortOrder || "descending",
          userIds: params.userIds?.join(",") || "",
          statuses: params.statuses?.join(",") || "",
          // Resolved here rather than stored resolved: a preset means "the last
          // 30 days" whenever it is asked, and resolveDateFilter quantises the
          // window to whole days so the same question produces the same cache
          // key all day.
          ...(params.dateFilter
            ? (() => {
                const window = resolveDateFilter(params.dateFilter);
                return window
                  ? {
                      dateField: window.dateField,
                      dateFrom: window.dateFrom || "",
                      dateTo: window.dateTo || "",
                    }
                  : {};
              })()
            : {}),
          exceptId: params.primaryVersionId,
        },
      }),
      // Wrap array → { items, hasMore }
      transformResponse: (resp: IVersion[], _meta, arg) => {
        const size = arg.size ?? 25;
        return {
          items: resp,
          // If we got a full page, there may be more; if less than size, we've reached the end
          hasMore: resp.length === size,
        };
      },
      // One cache entry per (pageId + filters) — excluding `page`
      // JSON.stringify drops the undefined `page`: same key as before, minus the page.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}|${JSON.stringify({ ...queryArgs, page: undefined })}`,
      // Append new page results to the same cache entry
      merge: (currentCache, newCache, { arg }) => {
        if (!currentCache.items) currentCache.items = [];
        // If it's the first page, replace the items instead of appending
        if (arg.page === 0) {
          currentCache.items = newCache.items;
        } else {
          // avoid duplicate IDs if a refetch returns overlapping items
          const existing = new Set(currentCache.items.map((v) => v._id));
          const deduped = newCache.items.filter((v) => !existing.has(v._id));
          currentCache.items.push(...deduped);
        }
        currentCache.hasMore = newCache.hasMore;
      },
      // Refetch when any filter (including page) changes
      forceRefetch({ currentArg, previousArg }) {
        if (!currentArg || !previousArg) return true;
        return (
          currentArg.pageId !== previousArg.pageId ||
          currentArg.search !== previousArg.search ||
          currentArg.page !== previousArg.page ||
          currentArg.refreshSeq !== previousArg.refreshSeq ||
          JSON.stringify(currentArg.userIds) !==
            JSON.stringify(previousArg.userIds) ||
          currentArg.sortOrder !== previousArg.sortOrder ||
          currentArg.size !== previousArg.size
        );
      },
      providesTags: (_result, _err, arg) => [
        { type: "VersionSearch" as const, id: arg.pageId },
        // Global tag to allow iterating all cached filter combinations, when pageId is unknown
        { type: "VersionSearchAll" as const, id: "all" },
      ],
      keepUnusedDataFor: 60,
    }),
    createVersion: builder.mutation({
      query: ({
        id,
        data,
        expireAt,
        contentPointer = null,
        isPublic = true,
        duplicateFromCanonicalId = null,
        previewUrl = null,
        expiryReminderDaysBefore,
      }) => ({
        url: `/pages/v2/publish-version/${id}`,
        method: "POST",
        body: {
          contentPointer,
          data,
          isPublic,
          expireAt,
          expiryReminderDaysBefore,
          duplicateFromCanonicalId,
          previewUrl,
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled; // created: IVersion
          // Patch all cached lists for this pageId where the created item matches filters
          const created = data as IVersion;
          const pageId = (created as any).pageId ?? args.id;
          const state = getState() as RootState;
          const cachedArgs = getCachedSearchArgsForPage(state, pageId);
          for (const a of cachedArgs) {
            dispatch(
              versionsApi.util.updateQueryData(
                "searchVersionsPaged",
                a as any,
                (draft: TPagedVersions) => {
                  if (!matchesFiltersForArgs(created, a)) return;
                  const idx = draft.items.findIndex(
                    (v) => v._id === created._id,
                  );
                  if (idx >= 0) draft.items[idx] = created;
                  else if (a.sortOrder === "ascending")
                    draft.items.push(created);
                  else draft.items.unshift(created);
                },
              ),
            );
          }

          // invalidate dashboard/story module's version search
          void reactQueryUtils.invalidateQueries([...versionsKeys.all]);

          // update the legacy redux store as well --- FIXME remove this
          dispatch(createVersion(created));
        } catch {
          // The send-flow builder toasts its own failure.
          if (!args.silent) toast.error("Failed create version");
        }
      },
      invalidatesTags: [
        { type: "Versions" },
        { type: "Versions", id: "counts-by-page" },
      ],
    }),
    updateVersion: builder.mutation<IVersion, TUpdateVersionArgs>({
      query: ({
        versionId,
        payload,
        isSyncToMaster = false,
        expireAt,
        expiryReminderDaysBefore,
      }: TUpdateVersionArgs) => ({
        url: path(`/v2/update-version/${versionId}`),
        method: "PUT",
        body: {
          payload,
          flags: {
            isSyncToMaster,
            // Send flow: no expireAt = keep the version's (the server's rule),
            // so a preview refresh or a resync doesn't silently drop the expiry
            // and its reminder. Off, as before: a missing expireAt clears it.
            expireAt:
              expireAt === undefined && !isOnFeatureFlag(FEATURE_FLAGS.SEND_FLOW)
                ? null
                : expireAt,
            expiryReminderDaysBefore,
          },
        },
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: updated } = await queryFulfilled; // updated: IVersion

          // Patch all cached lists for the updated.pageId
          const state = getState() as RootState;
          const pageId = updated.pageId;
          const cachedArgs = getCachedSearchArgsForPage(state, pageId);
          for (const a of cachedArgs) {
            dispatch(
              versionsApi.util.updateQueryData(
                "searchVersionsPaged",
                a as any,
                (draft: TPagedVersions) => {
                  const i = draft.items.findIndex((v) => v._id === updated._id);
                  const matches = matchesFiltersForArgs(updated, a);
                  if (i >= 0) {
                    if (matches) draft.items[i] = updated;
                    else draft.items.splice(i, 1);
                  } else if (matches) {
                    if (a.sortOrder === "ascending") draft.items.push(updated);
                    else draft.items.unshift(updated);
                  }
                },
              ),
            );
          }

          // invalidate dashboard/story module's version search
          void reactQueryUtils.invalidateQueries([...versionsKeys.all]);

          // update the legacy redux store as well --- FIXME remove this
          dispatch(updateVersion(updated));
          dispatch(
            updatePageOgImage({
              ogImage: updated.data?.ogImage,
              versionsId: updated._id,
            }),
          );

          // If the editor is currently viewing this version, sync the
          // `story.version` slice so canvas-side variable resolution and the
          // settings-panel pills pick up the new data (title, variable values,
          // password/expiry flags) without a full reload.
          if (state.story?.version?._id === updated._id) {
            dispatch(setStoryVersion(updated));
          }
        } catch {
          if (!args.silent) toast.error("Failed update version");
        }
      },
      invalidatesTags: (_result, _error, { versionId }) => [
        { type: "Version", id: versionId },
      ],
    }),
    // Typed response: POST /versions/v2/:id returns the stored version
    // document, and `use-update-preview-only` reads `contentPointer` off it to
    // adopt the head the backend's CAS actually kept. Untyped, that read was
    // `any` and needed a cast to be usable.
    updateVersionInDatabase: builder.mutation<
      IVersion,
      { versionId: string; payload: Record<string, unknown> }
    >({
      query: ({ versionId, payload }) => ({
        url: path(`/v2/${versionId}`),
        method: "POST",
        body: payload,
      }),
    }),
    // Send flow: the signed-in person's email sends for the period (Figma
    // 468:44889): Team counts by day, every other plan by month.
    getSendQuota: builder.query<TSendQuota, void>({
      query: () => ({ url: path("/send-quota") }),
      providesTags: ["SendQuota"],
    }),
    // Send flow: email the version's live link; each recipient is one send.
    sendVersion: builder.mutation<
      { sendId: string; count: number },
      {
        versionId: string;
        emails: string[];
        message?: string;
        reminder?: { everyDays: number };
      }
    >({
      query: ({ versionId, ...body }) => ({
        url: path(`/${versionId}/send`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["SendQuota"],
    }),
    archiveVersion: builder.mutation({
      query: ({ id }: { id: string; multiselect?: boolean }) => ({
        url: path(`/${id}`),
        method: "DELETE",
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const versions: IVersion[] = (getState() as RootState).shareDialog
          .versions;
        const version: any = versions.find((v: IVersion) => v._id === args.id);
        if (version && !args.multiselect) {
          dispatch(
            updateVersion({
              ...version,
              expireAt: new Date().toISOString(),
              url: null,
            }),
          );
        }
        try {
          const { data } = await queryFulfilled;
          dispatch(updateVersion(data));

          // invalidate dashboard/story module's version search
          void reactQueryUtils.invalidateQueries([...versionsKeys.all]);

          // New cache maintenance: archive is an update (not removal)
          const updated = data as IVersion;
          const state = getState() as RootState;
          const pageId = (updated as any).pageId;
          const cachedArgs = getCachedSearchArgsForPage(state, pageId);
          for (const a of cachedArgs) {
            dispatch(
              versionsApi.util.updateQueryData(
                "searchVersionsPaged",
                a as any,
                (draft: TPagedVersions) => {
                  const i = draft.items.findIndex((v) => v._id === updated._id);
                  if (i >= 0) draft.items[i] = updated;
                },
              ),
            );
          }
          if (!args.multiselect) {
            toast.success("Version archived successfully");
          }
        } catch {
          // Silent under multiselect: the caller reports the run as a whole, so
          // a partly failed selection would otherwise raise one toast per
          // failure on top of that summary.
          if (!args.multiselect) toast.error("Failed to archive version");
          if (version) dispatch(updateVersion(version));
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Version", id },
        { type: "Versions" },
        { type: "Versions", id: "counts-by-page" },
      ],
    }),
    deleteVersion: builder.mutation({
      query: ({ id }: { id: string; multiselect?: boolean }) => ({
        url: path(`/${id}/permanently`),
        method: "DELETE",
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const versions: IVersion[] = (getState() as RootState).shareDialog
          .versions;
        const versionIndex: any = versions.findIndex(
          (v: IVersion) => v._id === args.id,
        );
        if (versionIndex >= 0 && !args.multiselect) {
          dispatch(deleteVersion(versions[versionIndex]._id));
        }
        try {
          await queryFulfilled;
          // New cache maintenance: remove the item from all cached filter combinations
          const state = getState() as RootState;
          const cachedArgs = getAllCachedSearchArgs(state);
          for (const a of cachedArgs) {
            dispatch(
              versionsApi.util.updateQueryData(
                "searchVersionsPaged",
                a as any,
                (draft: TPagedVersions) => {
                  const i = draft.items.findIndex((v) => v._id === args.id);
                  if (i >= 0) draft.items.splice(i, 1);
                },
              ),
            );
          }
          if (versionIndex >= 0 && args.multiselect) {
            dispatch(deleteVersion(versions[versionIndex]._id));
          }
          // invalidate dashboard/story module's version search
          void reactQueryUtils.invalidateQueries([...versionsKeys.all]);
        } catch {
          if (!args.multiselect) toast.error("Failed to delete version");
          versions.splice(versionIndex, 0, versions[versionIndex]);
          if (versionIndex >= 0) dispatch(setVersions(versions));
        }
      },
      invalidatesTags: [
        { type: "Versions" },
        { type: "Versions", id: "counts-by-page" },
      ],
    }),
    deleteVersionFromBucket: builder.mutation({
      query: ({ id }) => ({
        url: path(`/${id}`),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Versions", id: "total-count" },
        { type: "Versions", id: "count" },
        { type: "Version", id },
      ],
    }),
    getSignedPostPolicyFromBucket: builder.query({
      query: ({ type, bucket = null, folder = "", purpose, signal }) => ({
        url: "/pages/getSignedPostPolicyFromBucket",
        method: "GET",
        params: {
          contentType: type,
          // `bucket` is ignored by the backend now — the purpose decides the
          // destination. Kept for backwards compatibility with old callers.
          bucket,
          folder,
          ...(purpose ? { purpose } : {}),
        },
        signal,
      }),
    }),
    getSignedPostPolicy: builder.query({
      query: ({ type }) => ({
        url: "/pages/getSignedPostPolicy",
        method: "GET",
        params: {
          contentType: type,
        },
      }),
    }),
    bulkSend: builder.mutation({
      query: ({ id, payload = {} }) => ({
        url: `/pages/${id}/bulkSend`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "VersionSearch" as const, id },
      ],
    }),
    // Send flow bulk create: the job POST bulkSend started, polled by the
    // story's Versions list.
    getBulkSendJob: builder.query<TBulkSendJob, { id: string; jobId: string }>({
      query: ({ id, jobId }) => ({
        url: `/pages/${id}/bulkSend/${jobId}`,
        method: "GET",
      }),
    }),
    getTotalVersionsCount: builder.query({
      query: ({ period = 30 }) => ({
        url: path("/count"),
        method: "GET",
        params: {
          period,
        },
      }),
      providesTags: [{ type: "Versions", id: "total-count" }],
    }),
    getAllVersionsCount: builder.query({
      query: () => ({
        url: path("/count"),
        method: "GET",
      }),
    }),
    getCreatorVersionsCount: builder.query({
      query: () => ({
        url: path("/creator-count"),
        method: "GET",
      }),
    }),
    getHeadlessBrowserOgLimit: builder.query({
      query: () => ({ url: path("/headless-browser-og-limit") }),
    }),
    getVersionsCount: builder.query<
      TGetVersionsCountResponse,
      TGetVersionsCountParams
    >({
      query: ({
        pageId,
        omitSealed = false,
        status = "",
        userId = "",
      }: TGetVersionsCountParams) => ({
        url: path("/count"),
        method: "GET",
        params: {
          pageId,
          status,
          omitSealed,
          userId,
        },
      }),
      providesTags: [{ type: "Versions", id: "count" }],
    }),
    getVersionCountsByPage: builder.query({
      query: () => ({
        url: path("/counts-by-page"),
        method: "GET",
      }),
      providesTags: [{ type: "Versions", id: "counts-by-page" }],
    }),
  }),
});

export const {
  useSearchVersionsForImportQuery,
  useGetVersionByIdQuery,
  useLazyGetVersionByIdQuery,
  useGetPageVersionsQuery,
  useSearchVersionsQuery,
  useCreateVersionMutation,
  useUpdateVersionMutation,
  useUpdateVersionInDatabaseMutation,
  useSendVersionMutation,
  useGetSendQuotaQuery,
  useArchiveVersionMutation,
  useDeleteVersionMutation,
  useLazySearchVersionsQuery,
  useSearchVersionsPagedQuery,
  useLazySearchVersionsPagedQuery,
  useLazyGetSignedPostPolicyFromBucketQuery,
  useLazyGetSignedPostPolicyQuery,
  useBulkSendMutation,
  useGetBulkSendJobQuery,
  useGetTotalVersionsCountQuery,
  useGetCreatorVersionsCountQuery,
  useGetHeadlessBrowserOgLimitQuery,
  useGetVersionsCountQuery,
  useLazyGetPageVersionsQuery,
  useGetAllVersionsCountQuery,
  useGetVersionCountsByPageQuery,
  useLazyGetVersionCountsByPageQuery,
  useDeleteVersionFromBucketMutation,
} = versionsApi;
